import { CommonModule } from '@angular/common';
import { Component, ElementRef, NgModule, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, RouterModule } from '@angular/router';
import { applyLanguageFilteringMulti } from '@app/common/filtering-behavior';
import { Utilities } from '@app/common/utilities';
import { ActionRouterLinkModule } from '@app/components/actions/action-router-link/action-router-link.component';
import { AlertTemplatesComponent } from '@app/components/alert-templates/alert-templates.component';
import { AppTileBoxModule } from '@app/components/apps-and-tools/app-tile-box/app-tile-box.component';
import { ErrorTemplatesModule } from '@app/components/error-templates/error-templates.component';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { UserAppTile } from '@app/models/user-app-tile';
import { LinkModule } from '@app/directives/link.directive';
import { ComponentStatesOnRoutesService } from '@app/services/component-states-on-routes.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { UserAppTileService } from '@app/services/user-app-tile.service';
import { UserLanguageTermService } from '@app/services/user-language-term.service';
import { SortablejsModule } from 'ngx-sortablejs';
import { BehaviorSubject, combineLatest, iif, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Options } from 'sortablejs';

@Component({
  selector: 'app-my-app-tiles',
  templateUrl: './my-app-tiles.component.html',
  styleUrls: ['./my-app-tiles.component.scss']
})
export class MyAppTilesComponent implements OnInit, OnDestroy {

  userAppTiles$: Observable<UserAppTile[]>;
  userAppTiles: UserAppTile[];
  editMode = false;
  showHidden = false;
  componentName = 'Apps & tools';
  options: Options;
  private showHiddenSubject = new BehaviorSubject<boolean>(this.showHidden);
  private errorSubject$ = new Subject();
  updateUserAppTileSubscription: Subscription;
  dialogSubscription: Subscription;
  @ViewChild('allappsandtoolssnackbarorder') orderUpdatedTmpl: TemplateRef<any>;

  constructor(private userAppTileService: UserAppTileService,
              private snackBar: MatSnackBar,
              private componentEventService: ComponentStatesOnRoutesService,
              private featureSettingsStoreService: FeatureSettingsStoreService,
              private userLanguageTermService: UserLanguageTermService,
              private router: Router,
              public elementRef: ElementRef) {

    this.options = {
      disabled: false,
      filter: '.ignore-elements',
      onUpdate: (event: any) => {
        this.onSortUpdate();
      }
    };
  }

  handleRemove(event: UserAppTile, userAppTiles: UserAppTile[]) {
    if (event) {
      if (this.showHidden && event.HideFromUI) {
        // Do nothing in this case becasue the app will be hidden and we are already showing hidden apps.
      } else {
        const index = userAppTiles.findIndex(d => d.AppTileId === event.AppTileId);
        if (index !== -1) {
          userAppTiles.splice(index, 1);
        }
      }
    }
  }

  private onSortUpdate() {
    this.userAppTiles.forEach((link, index) => {
      link.Order = index;
    });

    this.updateUserAppTileSubscription = this.userAppTileService.updateUserAppTiles(this.userAppTiles)
      .subscribe(() => {
        Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.updatedorder);
      });
  }

  // The type here is any beacuse it could be MatSlideToggleChange as well keyboard key evnets
  handleEditMode(change: any) {
    if (this.editMode && !change.checked && this.showHidden) {
      this.showHidden = false;
      this.showHiddenSubject.next(this.showHidden);
    }
    this.editMode = change.checked;
    // key board right arrow event
    if (change.keyCode && change.keyCode === 39) {
      this.editMode = true;
    }
    // key board left arrow event
    if (change.keyCode && change.keyCode === 37) {
      this.editMode = false;
    }

    this.userAppTileService.refresh$.next(null);
  }

  // The type here is any beacuse it could be MatSlideToggleChange as well keyboard key evnets
  handleShowHidden(change: any) {
    this.showHidden = change.checked;
    // key board right arrow event
    if (change.keyCode && change.keyCode === 39) {
      this.showHidden = true;
    }
    // key board left arrow event
    if (change.keyCode && change.keyCode === 37) {
      this.showHidden = false;
    }

    this.showHiddenSubject.next(this.showHidden);
  }

  ngOnInit() {
    const componentEvent = this.componentEventService.state.RouteMap.has(this.router.url) && this.componentEventService.state.RouteMap.get(this.router.url).find(x => x.ComponentId === this.elementRef.nativeElement.localName);
    const componentEventData = componentEvent.Data as Array<UserAppTile>;

    const userAppTiles$ = combineLatest([this.showHiddenSubject, this.userAppTileService.refresh$])
      .pipe(
        mergeMap(([includeHidden]) => this.userAppTileService.getLicensedUserAppTiles(includeHidden)),
        tap(result => {
          return result.map(userAppTile => {
            if (componentEventData) {
              const foundAppTile = componentEventData.find(ced => ced.AppTileId === userAppTile.AppTileId);
              if (foundAppTile) {
                userAppTile.ShowNewOrModifiedBadge = true;
              }
            } else {
              userAppTile.ShowNewOrModifiedBadge = false;
            }
            return userAppTile;
          });
        }));

    this.userAppTiles$ = userAppTiles$
      .pipe(
        switchMap((userAppTiles) => {
          // Language filter toggle
          return iif(
            () => this.featureSettingsStoreService.state.enableLanguageFilteringForWorkplace,
            this.userLanguageTermService.getUserLanguageTerms().pipe(
              map((userLanguageTerms) => {
                // Don't filter out personal app tiles or subscribed app tiles
                const personalAppTiles = userAppTiles.filter(u => u.AppTile.IsPersonal);
                const nonPersonalAppTiles = applyLanguageFilteringMulti(userAppTiles.filter(u => !u.AppTile.IsPersonal), 'AppTile.LanguageTermAppTiles', userLanguageTerms, this.featureSettingsStoreService.state.enableShowNonLanguageContentForAllUsers, this.featureSettingsStoreService.state.defaultLanguageTermId);
                return nonPersonalAppTiles.concat(personalAppTiles).sort((a, b) => {
                  return a.Order - b.Order;
                });
              })
            ),
            of(userAppTiles)
          );
        }),
        map((userAppTiles) => {
          this.userAppTiles = userAppTiles;
          return userAppTiles;
        }),
        catchError(err => {
          this.errorSubject$.next(err);
          return of(null);
        }));
  }

  ngOnDestroy(): void {
    this.updateUserAppTileSubscription && this.updateUserAppTileSubscription.unsubscribe();
    this.dialogSubscription && this.dialogSubscription.unsubscribe();
  }

}
@NgModule({
  declarations: [MyAppTilesComponent],
  exports: [MyAppTilesComponent],
  imports: [
    CommonModule,
    TextPlaceholderModule,
    AppTileBoxModule,
    MatButtonModule,
    ErrorTemplatesModule,
    ActionRouterLinkModule,
    MatSlideToggleModule,
    WidgetPlaceholderModule,
    SortablejsModule,
    LinkModule,
    RouterModule,
  ]
})
export class MyAppTilesModule { }
